import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { loadHtml } from "../../service/htmlCache";

/**
 * Renders an editable HTML snippet with click delegation and optional
 * {{placeholder}} substitution.
 *
 * The snippet is resolved by key from window.__APP_HTML, populated by
 * /public/content/<key>.js files (see service/htmlCache.js). Loading
 * resolves synchronously since all content is shipped with the bundle
 * and already in memory by the time this component mounts.
 *
 * Props:
 *  - contentKey: key in window.__APP_HTML (e.g. "about_ru").
 *  - actions: optional map from data-action values to Redux actions.
 *      A single click listener on the container dispatches the matching
 *      action when a descendant element with data-action is clicked.
 *  - placeholders: optional map of {{name}} -> replacement string; the
 *      substitution is applied to the loaded HTML before rendering.
 */
const HtmlContent = ({ contentKey, actions, placeholders }) => {
    const dispatch = useDispatch();
    const [html, setHtml] = useState('');
    const containerRef = useRef(null);

    // Keep actions in a ref so the click listener always sees the latest map
    // without being re-attached on every render of the parent component.
    const actionsRef = useRef(actions);
    actionsRef.current = actions;

    // A string key that changes only when the actual placeholder values
    // change — prevents redundant re-renders when the parent passes a new
    // object with identical contents.
    const placeholdersKey = placeholders ? JSON.stringify(placeholders) : '';

    useEffect(() => {
        let cancelled = false;
        loadHtml(contentKey)
            .then((text) => {
                let out = text;
                if (placeholders) {
                    for (const key of Object.keys(placeholders)) {
                        const value = placeholders[key] ?? '';
                        out = out.split(`{{${key}}}`).join(value);
                    }
                }
                if (!cancelled) setHtml(out);
            })
            .catch((err) => {
                console.error(`HtmlContent - failed to load ${contentKey}:`, err);
                if (!cancelled) setHtml('');
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentKey, placeholdersKey]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const onClick = (e) => {
            const target = e.target.closest('[data-action]');
            if (!target || !el.contains(target)) return;
            const action = actionsRef.current?.[target.dataset.action];
            if (action) dispatch(action);
        };
        el.addEventListener('click', onClick);
        return () => el.removeEventListener('click', onClick);
    }, [dispatch]);

    return (
        <div ref={containerRef} dangerouslySetInnerHTML={{ __html: html }} />
    );
};

export default HtmlContent;
